import { type CommandHandler } from '@telespot/core';
import { RegisterOrganization } from './register-organization.command';
import {
  type AffiliateRepository,
  ILicenseInfo,
  Organization,
  type OrganizationRepository,
  User,
  type UserRepository,
} from '@telespot/domain';

/**
 * Handler interface for the {@link RegisterOrganization} command.
 *
 * @group Organizations
 * @category Handlers
 */
export type IRegisterOrganizationHandler = CommandHandler<RegisterOrganization>;

/**
 * @group Organizations
 * @category Errors
 */
export enum RegisterOrganizationErrors {
  ORGANIZATION_NOT_FOUND = 'Organization not found',
  USER_NOT_FOUND = 'Authenticated user not found',
  AFFILIATION_ERROR = 'Error affiliating current user to newly created organization. Organization was removed to prevent inconsistencies',
  SAVE_ORGANIZATION_ERROR = 'Error saving organization',
}

/**
 * Default implementation of the {@link IRegisterOrganizationHandler} interface.
 *
 * @group Organizations
 * @category Handlers
 */
export class RegisterOrganizationHandler implements IRegisterOrganizationHandler {
  constructor(
    private readonly organizationRepository: OrganizationRepository,
    private readonly affiliateRepository: AffiliateRepository,
    private readonly userRepository: UserRepository
  ) {}

  private async fetchExistingOrganization(organizationId: string): Promise<Organization> {
    try {
      const organization = await this.organizationRepository.getById(organizationId);
      if (!organization) {
        throw new Error(RegisterOrganizationErrors.ORGANIZATION_NOT_FOUND);
      }
      return organization;
    } catch (error) {
      throw new Error(RegisterOrganizationErrors.ORGANIZATION_NOT_FOUND);
    }
  }

  private async saveOrganization(
    organizationId: string | undefined,
    name: string,
    license: ILicenseInfo,
    logo?: File
  ): Promise<{ id: string; organization: Organization }> {
    if (organizationId) {
      const organization = await this.fetchExistingOrganization(organizationId);
      organization.changeLicense(license);
      organization.changeName(name);
      await this.organizationRepository.save(organization, logo);
      return { id: organization.id, organization };
    } else {
      const organization = new Organization({
        name,
        logo: undefined,
        license: license,
        logoUrl: undefined,
        usedStorage: 0,
        members: 1,
      });
      const id = await this.organizationRepository.save(organization, logo);
      return { id, organization };
    }
  }

  private async retrieveCurrentUser(userId: string): Promise<User> {
    try {
      const user = await this.userRepository.getById(userId);
      if (!user) {
        throw new Error(RegisterOrganizationErrors.USER_NOT_FOUND);
      }
      return user;
    } catch (error) {
      throw new Error(RegisterOrganizationErrors.USER_NOT_FOUND);
    }
  }

  private async affiliateCurrentUser(
    organizationId: string,
    organization: Organization,
    userId: string
  ): Promise<boolean> {
    try {
      const user = await this.retrieveCurrentUser(userId);
      const affiliate = organization.addUser(user, organizationId);
      await this.affiliateRepository.save(affiliate);
      return true;
    } catch (error) {
      return false;
    }
  }

  public async execute(command: RegisterOrganization): Promise<string> {
    const { organizationId, name, logo, license, userId } = command.details;

    try {
      const { id: savedOrganizationId, organization: savedOrganization } = await this.saveOrganization(
        organizationId,
        name,
        license,
        logo
      );

      // Handle user affiliation if the organization was new
      if (!organizationId) {
        const affiliated = await this.affiliateCurrentUser(savedOrganizationId, savedOrganization, userId);
        if (!affiliated) {
          await this.organizationRepository.delete(savedOrganizationId);
          throw new Error(RegisterOrganizationErrors.AFFILIATION_ERROR);
        }
      }

      return savedOrganization.id;
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.message === RegisterOrganizationErrors.AFFILIATION_ERROR) throw error;
        throw new Error(RegisterOrganizationErrors.SAVE_ORGANIZATION_ERROR);
      }

      throw new Error(RegisterOrganizationErrors.SAVE_ORGANIZATION_ERROR);
    }
  }
}
